import { createContext, useContext, useEffect, useState } from 'react';
import { jwtDecode } from 'jwt-decode'; 
import { getCurrentUser, updateLastActive, isOnline } from 'src/utils';
import { setLogoutCallback } from '../auth-logout-callback';
import { refreshToken as refreshTokenService } from '../services/refresh-token';
import { Capacitor } from '@capacitor/core';
import {
  setNativeRefreshToken,
  removeNativeRefreshToken,
  getNativeRefreshToken,
} from 'src/utils/native-secure-storage';
import LogRocket from 'logrocket';

const isNativeApp = Capacitor.isNativePlatform();
const CACHED_USER_KEY = 'cachedUser';
const ACCESS_TOKEN_KEY = 'token'; 

const AuthContext = createContext();

function isTokenExpired(token) {
  if (!token) return true;
  try {
    const decoded = jwtDecode(token);
    if (!decoded?.exp) return true;
    return decoded.exp * 1000 < Date.now();
  } catch (e) {
    console.warn('Failed to decode token', e);
    return true;
  }
}

function readCachedUser() {
  try {
    const raw = localStorage.getItem(CACHED_USER_KEY);
    return raw ? JSON.parse(raw) : null;
  } catch (e) {
    console.error('Failed to read cached user', e);
    return null;
  }
}

function writeCachedUser(user) {
  try {
    localStorage.setItem(CACHED_USER_KEY, JSON.stringify(user));
  } catch (e) {
    console.error('Failed to write cached user', e);
  }
}

function removeCachedUser() {
  try {
    localStorage.removeItem(CACHED_USER_KEY);
  } catch (e) {
    console.error('Failed to remove cached user', e);
  }
}

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [isFetching, setIsFetching] = useState(false);
  const [isAuthReady, setIsAuthReady] = useState(false);

  useEffect(() => {
    setLogoutCallback(logout);
  }, []);

  useEffect(() => {
    if (!import.meta.env.PROD) return;

    if (!user?.id) {
      if (user === null) {
        LogRocket.identify('anonymous');
      }
      return;
    }

    LogRocket.identify(user.id.toString(), {
      name: user.name || 'Unknown User',
      email: user.email || '',
    });
  }, [user]);

  useEffect(() => {
    const initializeAuth = async () => {
      try {
        let token = localStorage.getItem(ACCESS_TOKEN_KEY);

        if (!token && isNativeApp) {
          const nativeRefresh = await getNativeRefreshToken();
          if (nativeRefresh) {
            try {
              const refreshRes = await refreshTokenService(nativeRefresh);
              const newAccessToken = typeof refreshRes === 'string' ? refreshRes : refreshRes?.accessToken || refreshRes;
              const maybeRefreshToken = typeof refreshRes === 'object' && refreshRes?.refreshToken ? refreshRes.refreshToken : null;
              if (newAccessToken) {
                localStorage.setItem(ACCESS_TOKEN_KEY, newAccessToken);
                token = newAccessToken;
                if (maybeRefreshToken) {
                  await setNativeRefreshToken(maybeRefreshToken);
                }
              }
            } catch (e) {
              console.warn('Native refresh failed during init', e);
            }
          }
        }

        if (!token) {
          logout();
          return;
        }

        const expired = isTokenExpired(token);

        if (expired) {
          if (isOnline()) {
            let secureRefresh = null;
            if (isNativeApp) {
              secureRefresh = await getNativeRefreshToken();
            }
            try {
              const refreshRes = await refreshTokenService(secureRefresh);
              const newAccessToken = typeof refreshRes === 'string' ? refreshRes : refreshRes?.accessToken || refreshRes;
              const newRefreshToken = typeof refreshRes === 'object' ? refreshRes?.refreshToken : null;

              if (newAccessToken) {
                await loginUser(newAccessToken, newRefreshToken);
              } else {
                console.warn('Refresh returned no access token at init');
                const cached = readCachedUser();
                if (cached) {
                  setUser(cached);
                } else {
                  logout();
                }
              }
            } catch (e) {
              console.warn('Token refresh during init failed', e);
              const cached = readCachedUser();
              if (cached) {
                setUser(cached);
              } else {
                logout();
              }
            }
          } else {
            const cached = readCachedUser();
            if (cached) {
              console.log('Offline + expired token -> using cached user');
              setUser(cached);
            } else {
              try {
                const decoded = jwtDecode(token);
                console.log('Offline + expired token -> using decoded JWT as fallback');
                setUser(decoded);
              } catch (e) {
                console.warn('Failed to decode token during offline init', e);
                logout();
              }
            }
          }
        } else {
          if (isOnline()) {
            await fetchUser();
          } else {
            const cached = readCachedUser();
            if (cached) {
              setUser(cached);
            } else {
              try {
                const decoded = jwtDecode(token);
                setUser(decoded);
              } catch (e) {
                console.warn('Failed to decode token during offline not-expired init', e);
                logout();
              }
            }
          }
        }
      } catch (e) {
        console.error('Auth init error', e);
        logout();
      } finally {
        setIsAuthReady(true);
      }
    };

    initializeAuth();
  }, []);

  const fetchUser = async () => {
    setIsFetching(true);
    try {
      const res = await getCurrentUser();
      if (res) {
        setUser(res);
        writeCachedUser(res); 
          if (isOnline()) {
            const lastActive = res.lastActiveAt; 
            const now = Date.now();
            const fifteenMinutes = 15 * 60 * 1000;

            const shouldUpdate = !lastActive || (now - new Date(lastActive).getTime()) > fifteenMinutes;

            if (shouldUpdate) {
              updateLastActive();
            }
        }
      } else {
        throw new Error('Empty /me response');
      }
    } catch (err) {
      console.error('Failed to fetch user info', err);
      if (!isOnline()) {
        console.log('Offline -> using cached user (if present)');
        const cached = readCachedUser();
        if (cached) {
          setUser(cached);
          return;
        }

        const token = localStorage.getItem(ACCESS_TOKEN_KEY);
        if (token) {
          try {
            const decoded = jwtDecode(token);
            setUser(decoded);
            return;
          } catch (e) {
            console.warn('Failed to decode token in fetchUser offline fallback', e);
          }
        }
      } else {
        if (err?.response?.status === 401) {
          logout();
        }
      }
    } finally {
      setIsFetching(false);
    }
  };

  const loginUser = async (accessToken, refreshToken) => {
    try {
      if (!accessToken) throw new Error('No access token passed to loginUser');
      localStorage.setItem(ACCESS_TOKEN_KEY, accessToken);
      const decoded = jwtDecode(accessToken);
      setUser(decoded);

      if (isNativeApp && refreshToken) {
        try {
          await setNativeRefreshToken(refreshToken);
        } catch (e) {
          console.warn('setNativeRefreshToken failed', e);
        }
      }

      await fetchUser();
    } catch (e) {
      console.error('loginUser error', e);
      throw e;
    }
  };

  const logout = async () => {
    try {
      localStorage.removeItem(ACCESS_TOKEN_KEY);
      removeCachedUser();
      setUser(null);
      if (isNativeApp) {
        await removeNativeRefreshToken();
      }
    } catch (e) {
      console.error('logout error', e);
    }
  };

  return (
    <AuthContext.Provider
      value={{ user, loginUser, logout, setUser, fetchUser, isFetching, isAuthReady }}
    >
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
}
